import { useAtom, useAtomValue } from 'jotai';
import { Button } from '@carbon/react';
import { OpenPanelFilledLeft, OpenPanelLeft } from '@carbon/react/icons';
import { crossSectionArmedAtom, leftRailCollapsedAtom } from './atoms';
import { CrossSectionRailSection } from './cross-section-rail-section';
import styles from './cross-section-rail.module.scss';

/**
 * The section's display rail, for a page that is all map and has no rail of its own.
 *
 * This is where how it LOOKS is decided — colours, and the filters that choose which
 * wells take part. What the section IS lives in the settings panel behind the drawer's
 * gear. That split is the rule, and it is why removing a grid happens there while
 * recolouring one happens here.
 *
 * A page that already has a display rail — Map Query, with its dataset sidebar — puts
 * `CrossSectionRailSection` into that instead of mounting this.
 *
 * @return {JSX.Element | null} The rail, its collapsed handle, or nothing.
 */
export const CrossSectionRail = (): JSX.Element | null => {
  const armed = useAtomValue(crossSectionArmedAtom);
  const [collapsed, setCollapsed] = useAtom(leftRailCollapsedAtom);

  if (!armed) {
    return null;
  }

  if (collapsed) {
    return (
      <Button
        kind="ghost"
        size="sm"
        hasIconOnly
        renderIcon={OpenPanelLeft}
        iconDescription="Show cross section panel"
        tooltipPosition="right"
        className={styles.collapsed}
        onClick={() => setCollapsed(false)}
      />
    );
  }

  return (
    <aside className={styles.rail} aria-label="Cross section display">
      <div className={styles.header}>
        <h2>Cross Section</h2>
        <Button
          kind="ghost"
          size="sm"
          hasIconOnly
          renderIcon={OpenPanelFilledLeft}
          iconDescription="Hide cross section panel"
          tooltipPosition="left"
          onClick={() => setCollapsed(true)}
        />
      </div>
      <div className={styles.body}>
        <CrossSectionRailSection />
      </div>
    </aside>
  );
};
